import { BoardType, Color, Sport } from "@sportactics/board-editing";

import type { OnboardingUserType } from "../onboarding";
import type { UserRole } from "../user";
import { AnalyticsEventToken } from "./analytics-event-token";
import { AnalyticsEventType } from "./analytics-event-type";

export type AnalyticsEvent =
  | SelectBoardTypeAnalyticsEvent
  | ResetBoardAnalyticsEvent
  | AddTokenAnalyticsEvent
  | MoveTokenAnalyticsEvent
  | RemoveTokenAnalyticsEvent
  | DrawDrawingAnalyticsEvent
  | EraseDrawingAnalyticsEvent
  | EraseAllDrawingsAnalyticsEvent
  | DrawLineAnalyticsEvent
  | EraseLineAnalyticsEvent
  | EraseAllLinesAnalyticsEvent
  | WriteTextAnalyticsEvent
  | EditTextAnalyticsEvent
  | ChangeTextColorAnalyticsEvent
  | ChangeTextSizeAnalyticsEvent
  | MoveTextAnalyticsEvent
  | RemoveTextAnalyticsEvent
  | RemoveAllTextsAnalyticsEvent
  | CreateTeamAnalyticsEvent
  | ChangeTeamNameAnalyticsEvent
  | AddPlayerAnalyticsEvent
  | EditPlayerAnalyticsEvent
  | RemovePlayerAnalyticsEvent
  | RemoveTeamAnalyticsEvent
  | SaveAsNewFormationAnalyticsEvent
  | SaveAsFormationAnalyticsEvent
  | LoadFormationAnalyticsEvent
  | RenameFormationAnalyticsEvent
  | RemoveFormationAnalyticsEvent
  | ShareFormationAnalyticsEvent
  | ReceiveFormationAnalyticsEvent
  | PromptShareFormationAnalyticsEvent
  | TakeScreenshotAnalyticsEvent
  | ShareScreenshotAnalyticsEvent
  | PrintBoardAnalyticsEvent
  | OnboardingUserTypeSelectedAnalyticsEvent
  | OnboardingSportSelectedAnalyticsEvent
  | OnboardingRoleSelectedAnalyticsEvent
  | OnboardingBoardPurposesSelectedAnalyticsEvent
  | OnboardingBoardVenuesSelectedAnalyticsEvent
  | OnboardingCompletedAnalyticsEvent
  | TwentyBoardEventsAnalyticsEvent
  | PromptSignInAnalyticsEvent
  | AcceptPromptSignInAnalyticsEvent
  | SubmitFeedbackAnalyticsEvent
  | PromptPreferencesAnalyticsEvent
  | AcceptPromptPreferencesAnalyticsEvent
  | SeeLiveEditTogetherAnalyticsEvent
  | OpenLiveEditTogetherAnalyticsEvent
  | ClickSignInAnalyticsEvent
  | SignInAnalyticsEvent
  | SignUpAnalyticsEvent
  | SignOutAnalyticsEvent;

export enum ShareFormationMethod {
  WebShare = "WebShare",
  Fallback = "Fallback",
}

export enum SignInEntryPoint {
  Settings = "Settings",
  Onboarding = "Onboarding",
  Prompt = "Prompt",
}

export type SelectBoardTypeAnalyticsEvent =
  BaseAnalyticsEvent<AnalyticsEventType.SelectBoardType> & BoardTypeProperty;

export type ResetBoardAnalyticsEvent =
  BaseAnalyticsEvent<AnalyticsEventType.ResetBoard> & BoardTypeProperty;

export type AddTokenAnalyticsEvent =
  BaseAnalyticsEvent<AnalyticsEventType.AddToken> &
    BoardTypeProperty &
    TokenProperty;

export type MoveTokenAnalyticsEvent =
  BaseAnalyticsEvent<AnalyticsEventType.MoveToken> &
    BoardTypeProperty &
    TokenProperty;

export type RemoveTokenAnalyticsEvent =
  BaseAnalyticsEvent<AnalyticsEventType.RemoveToken> & BoardTypeProperty;

export type DrawDrawingAnalyticsEvent =
  BaseAnalyticsEvent<AnalyticsEventType.DrawDrawing> &
    BoardTypeProperty &
    ColorProperty;

export type EraseDrawingAnalyticsEvent =
  BaseAnalyticsEvent<AnalyticsEventType.EraseDrawing> & BoardTypeProperty;

export type EraseAllDrawingsAnalyticsEvent =
  BaseAnalyticsEvent<AnalyticsEventType.EraseAllDrawings> & BoardTypeProperty;

export type DrawLineAnalyticsEvent =
  BaseAnalyticsEvent<AnalyticsEventType.DrawLine> &
    BoardTypeProperty &
    ColorProperty;

export type EraseLineAnalyticsEvent =
  BaseAnalyticsEvent<AnalyticsEventType.EraseLine> & BoardTypeProperty;

export type EraseAllLinesAnalyticsEvent =
  BaseAnalyticsEvent<AnalyticsEventType.EraseAllLines> & BoardTypeProperty;

export type WriteTextAnalyticsEvent =
  BaseAnalyticsEvent<AnalyticsEventType.WriteText> &
    BoardTypeProperty &
    ColorProperty;

export type EditTextAnalyticsEvent =
  BaseAnalyticsEvent<AnalyticsEventType.EditText> & BoardTypeProperty;

export type ChangeTextColorAnalyticsEvent =
  BaseAnalyticsEvent<AnalyticsEventType.ChangeTextColor> &
    BoardTypeProperty &
    ColorProperty;

export type ChangeTextSizeAnalyticsEvent =
  BaseAnalyticsEvent<AnalyticsEventType.ChangeTextSize> & BoardTypeProperty;

export type MoveTextAnalyticsEvent =
  BaseAnalyticsEvent<AnalyticsEventType.MoveText> & BoardTypeProperty;

export type RemoveTextAnalyticsEvent =
  BaseAnalyticsEvent<AnalyticsEventType.RemoveText> & BoardTypeProperty;

export type RemoveAllTextsAnalyticsEvent =
  BaseAnalyticsEvent<AnalyticsEventType.RemoveAllTexts> & BoardTypeProperty;

export type CreateTeamAnalyticsEvent =
  BaseAnalyticsEvent<AnalyticsEventType.CreateTeam> & SportProperty;

export type ChangeTeamNameAnalyticsEvent =
  BaseAnalyticsEvent<AnalyticsEventType.ChangeTeamName> & SportProperty;

export type AddPlayerAnalyticsEvent =
  BaseAnalyticsEvent<AnalyticsEventType.AddPlayer> & SportProperty;

export type EditPlayerAnalyticsEvent =
  BaseAnalyticsEvent<AnalyticsEventType.EditPlayer> & SportProperty;

export type RemovePlayerAnalyticsEvent =
  BaseAnalyticsEvent<AnalyticsEventType.RemovePlayer> & SportProperty;

export type RemoveTeamAnalyticsEvent =
  BaseAnalyticsEvent<AnalyticsEventType.RemoveTeam> & SportProperty;

export type SaveAsNewFormationAnalyticsEvent =
  BaseAnalyticsEvent<AnalyticsEventType.SaveAsNewFormation> & SportProperty;

export type SaveAsFormationAnalyticsEvent =
  BaseAnalyticsEvent<AnalyticsEventType.SaveAsFormation> & SportProperty;

export type LoadFormationAnalyticsEvent =
  BaseAnalyticsEvent<AnalyticsEventType.LoadFormation> & SportProperty;

export type RenameFormationAnalyticsEvent =
  BaseAnalyticsEvent<AnalyticsEventType.RenameFormation> & SportProperty;

export type RemoveFormationAnalyticsEvent =
  BaseAnalyticsEvent<AnalyticsEventType.RemoveFormation> & SportProperty;

export type ShareFormationAnalyticsEvent =
  BaseAnalyticsEvent<AnalyticsEventType.ShareFormation> &
    SportProperty &
    ShareFormationMethodProperty;

export type ReceiveFormationAnalyticsEvent =
  BaseAnalyticsEvent<AnalyticsEventType.ReceiveFormation> & SportProperty;

export type PromptShareFormationAnalyticsEvent =
  BaseAnalyticsEvent<AnalyticsEventType.PromptShareFormation> & SportProperty;

export type TakeScreenshotAnalyticsEvent =
  BaseAnalyticsEvent<AnalyticsEventType.TakeScreenshot> & SportProperty;

export type ShareScreenshotAnalyticsEvent =
  BaseAnalyticsEvent<AnalyticsEventType.ShareScreenshot> & SportProperty;

export type PrintBoardAnalyticsEvent =
  BaseAnalyticsEvent<AnalyticsEventType.PrintBoard> & SportProperty;

export type OnboardingUserTypeSelectedAnalyticsEvent =
  BaseAnalyticsEvent<AnalyticsEventType.OnboardingUserTypeSelected> &
    OnboardingUserTypeProperty;

export type OnboardingSportSelectedAnalyticsEvent =
  BaseAnalyticsEvent<AnalyticsEventType.OnboardingSportSelected> &
    SportProperty;

export type OnboardingRoleSelectedAnalyticsEvent =
  BaseAnalyticsEvent<AnalyticsEventType.OnboardingRoleSelected> &
    UserRoleProperty;

export type OnboardingBoardPurposesSelectedAnalyticsEvent =
  BaseAnalyticsEvent<AnalyticsEventType.OnboardingBoardPurposesSelected>;

export type OnboardingBoardVenuesSelectedAnalyticsEvent =
  BaseAnalyticsEvent<AnalyticsEventType.OnboardingBoardVenuesSelected>;

export type OnboardingCompletedAnalyticsEvent =
  BaseAnalyticsEvent<AnalyticsEventType.OnboardingCompleted> &
    SportProperty &
    UserRoleProperty;

export type TwentyBoardEventsAnalyticsEvent =
  BaseAnalyticsEvent<AnalyticsEventType.TwentyBoardEvents>;

export type PromptSignInAnalyticsEvent =
  BaseAnalyticsEvent<AnalyticsEventType.PromptSignIn>;

export type AcceptPromptSignInAnalyticsEvent =
  BaseAnalyticsEvent<AnalyticsEventType.AcceptPromptSignIn>;

export type PromptPreferencesAnalyticsEvent =
  BaseAnalyticsEvent<AnalyticsEventType.PromptPreferences>;

export type AcceptPromptPreferencesAnalyticsEvent =
  BaseAnalyticsEvent<AnalyticsEventType.AcceptPromptPreferences>;

export type SubmitFeedbackAnalyticsEvent =
  BaseAnalyticsEvent<AnalyticsEventType.SubmitFeedback>;

export type SeeLiveEditTogetherAnalyticsEvent =
  BaseAnalyticsEvent<AnalyticsEventType.SeeLiveEditTogether>;

export type OpenLiveEditTogetherAnalyticsEvent =
  BaseAnalyticsEvent<AnalyticsEventType.OpenLiveEditTogether>;

export type ClickSignInAnalyticsEvent =
  BaseAnalyticsEvent<AnalyticsEventType.ClickSignIn> & SignInEntryPointProperty;

export type SignInAnalyticsEvent =
  BaseAnalyticsEvent<AnalyticsEventType.SignIn>;

export type SignUpAnalyticsEvent =
  BaseAnalyticsEvent<AnalyticsEventType.SignUp>;

export type SignOutAnalyticsEvent =
  BaseAnalyticsEvent<AnalyticsEventType.SignOut>;

type BaseAnalyticsEvent<T extends AnalyticsEventType> = {
  type: T;
};

type BoardTypeProperty = {
  boardType: BoardType;
};

type SportProperty = {
  sport: Sport;
};

type TokenProperty = {
  token: AnalyticsEventToken;
};

type ColorProperty = {
  color: Color;
};

type ShareFormationMethodProperty = {
  shareFormationMethod: ShareFormationMethod;
};

type UserRoleProperty = {
  userRole: UserRole;
};

type OnboardingUserTypeProperty = {
  onboardingUserType: OnboardingUserType;
};

type SignInEntryPointProperty = {
  signInEntryPoint: SignInEntryPoint;
};
